<script lang="ts">
  // Authentication banner (spec §10): verdict colour + SPF/DKIM/DMARC detail, the recipient
  // role chip and the `details` toggle (the parent renders `Details` when it is on).
  import type { MessageDetail } from '$lib/api/types';
  import { t } from '$lib/i18n';

  type AuthInfo = NonNullable<MessageDetail['auth']>;

  let {
    auth,
    role,
    details = $bindable(false),
  }: {
    auth: AuthInfo;
    role: 'to' | 'cc' | 'other' | '';
    details?: boolean;
  } = $props();

  const pass = $derived(auth.verdict === 'pass');
  const fail = $derived(auth.verdict === 'fail');
  const showAuth = $derived(!!(auth.spf || auth.dkim));
  const roleKey = $derived(role === 'to' ? 'roleTo' : role === 'cc' ? 'roleCc' : 'roleOther');
</script>

<div class="auth" class:pass class:fail>
  <span class="auth-dot"></span>
  <span class="auth-text">
    {t(pass ? 'authPass' : fail ? 'authFail' : 'authUnknown')}
    {#if showAuth}<small dir="ltr">SPF:{auth.spf ?? '-'} DKIM:{auth.dkim ?? '-'} DMARC:{auth.dmarc ?? '-'}</small>{/if}
  </span>
  {#if role}<span class="rcpt-role" class:cc={role === 'cc'}>{t(roleKey)}</span>{/if}
  <button type="button" class="auth-more" aria-expanded={details} onclick={() => (details = !details)}>{t('details')}</button>
</div>

<style>
  /* svelte-rwh28b (Reader) — auth banner */
  .auth {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    background: var(--surface-subtle);
    font-size: var(--text-sm);
    line-height: var(--leading-body);
    margin-bottom: var(--space-3)
  }

  .auth.pass {
    background: var(--success-100);
    color: var(--success-500)
  }

  .auth.fail {
    background: var(--danger-100);
    color: var(--danger-500)
  }

  .auth-dot {
    width: 8px;
    height: 8px;
    border-radius: var(--radius-pill);
    background: var(--neutral-400);
    flex: none
  }

  .auth.pass .auth-dot {
    background: var(--success-500)
  }

  .auth.fail .auth-dot {
    background: var(--danger-500)
  }

  .auth-text {
    flex: 1;
    min-width: 0;
    line-height: var(--leading-body)
  }

  .auth-text small {
    color: var(--text-muted);
    margin-inline-start: 6px
  }

  .auth-more {
    font-size: var(--text-xs);
    text-decoration: underline;
    color: inherit
  }

  .rcpt-role {
    font-size: var(--text-xs);
    color: var(--text-muted);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-badge);
    line-height: var(--leading-tight);
    padding: 2px 8px;
    white-space: nowrap
  }

  .rcpt-role.cc {
    color: var(--text-body);
    font-weight: 600
  }
</style>
